import React, { createContext, useContext, useState, useEffect } from 'react';

export type ColorTheme = 'light' | 'dark' | 'forest' | 'ocean' | 'retro';

interface ThemeContextType {
  theme: ColorTheme;
  setTheme: (t: ColorTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const themes: Record<ColorTheme, Record<string, string>> = {
  light: {
    '--bg': '#f5f5f5',
    '--bg-card': '#ffffff',
    '--bg-nav': '#ffffff',
    '--text': '#1a1a1a',
    '--text-muted': '#666666',
    '--primary': '#e63946',
    '--primary-hover': '#c1121f',
    '--border': '#e0e0e0',
    '--shadow': '0 2px 8px rgba(0,0,0,0.08)',
  },
  dark: {
    '--bg': '#1a1a2e',
    '--bg-card': '#16213e',
    '--bg-nav': '#0f3460',
    '--text': '#eaeaea',
    '--text-muted': '#a0a0b0',
    '--primary': '#e94560',
    '--primary-hover': '#c73652',
    '--border': '#2a2a4a',
    '--shadow': '0 2px 8px rgba(0,0,0,0.4)',
  },
  forest: {
    '--bg': '#1b2d1e',
    '--bg-card': '#243328',
    '--bg-nav': '#2d4a32',
    '--text': '#d4edda',
    '--text-muted': '#9dbda4',
    '--primary': '#52b788',
    '--primary-hover': '#3a9c6f',
    '--border': '#3a5c3f',
    '--shadow': '0 2px 8px rgba(0,0,0,0.3)',
  },
  ocean: {
    '--bg': '#03045e',
    '--bg-card': '#023e8a',
    '--bg-nav': '#0077b6',
    '--text': '#caf0f8',
    '--text-muted': '#90e0ef',
    '--primary': '#00b4d8',
    '--primary-hover': '#0096c7',
    '--border': '#0077b6',
    '--shadow': '0 2px 8px rgba(0,0,0,0.4)',
  },
  retro: {
    '--bg': '#2b2b2b',
    '--bg-card': '#3c3c3c',
    '--bg-nav': '#1c1c1c',
    '--text': '#f8f8f2',
    '--text-muted': '#b0b0a0',
    '--primary': '#f1fa8c',
    '--primary-hover': '#d4dc5f',
    '--border': '#555544',
    '--shadow': '0 2px 8px rgba(0,0,0,0.5)',
  },
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ColorTheme>(
    (localStorage.getItem('theme') as ColorTheme) || 'dark'
  );

  const applyTheme = (t: ColorTheme) => {
    const vars = themes[t];
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
    root.setAttribute('data-theme', t);
  };

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (t: ColorTheme) => {
    setThemeState(t);
    localStorage.setItem('theme', t);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export { themes };
